<script setup lang="ts">
    import { onMounted, watch, computed, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"

    import { usePnlsPageQueryV1 } from '@Quest/Pnls/queries-vue-composables'
    import { useDeletePnlCommandV1 } from '@Quest/Pnls/commands-vue-composables'

    import type { PnlsPageReadModel } from '@Quest/Pnls/readmodels'


    /* ---------------- route params ---------------- */

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))


    /* ---------------- query ---------------- */

    const {
        runPnlsPageQueryV1,
        modelPnlsPageQueryV1
    } = usePnlsPageQueryV1(workspaceId, strategyId)

    const rm = modelPnlsPageQueryV1 as Ref<PnlsPageReadModel | null>
    const pnlIdToDelete = ref<string | null>(null)


    /* ---------------- command ---------------- */

    const {
        invokeDeletePnlCommandV1,
        succeedDeletePnlCommandV1,
        invokingDeletePnlCommandV1
    } = useDeletePnlCommandV1(workspaceId, strategyId, pnlIdToDelete)


    /* ---------------- lifecycle ---------------- */

    onMounted(() =>
    {
        document.title = "P&L templates | TransparentChoice"
        runPnlsPageQueryV1()
    })


    /* ---------------- actions ---------------- */

    function handleDeletePnlCommand(pnlId: string)
    {
        if (invokingDeletePnlCommandV1.value) return

        if (confirm("Are you sure?") === true)
        {
            pnlIdToDelete.value = pnlId;
            invokeDeletePnlCommandV1()
        }
    }


    /* ---------------- command success handling ---------------- */

    watch(succeedDeletePnlCommandV1, (current, previous) =>
    {
        if (current && !previous && rm.value)
        {
            const id = pnlIdToDelete.value
            if (!id) return

            rm.value.Corporate = rm.value.Corporate.filter(a => a.Id !== id)
            pnlIdToDelete.value = null
        }
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="strategy" />

        <div v-if="rm" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">                    
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">P&L templates</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <div class="btn-group" v-if="rm.Defaults">
                        <button type="button" class="btn btn-outline-success dropdown-toggle" data-toggle="dropdown" style="padding: 0.7em 2em;">Create P&L</button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/pnls/create/${workspaceId}/${strategyId}`">
                                Blank
                            </a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" v-for="t in rm.Defaults" :key="t.Id" v-sanitizedHref="`/#/enterprise/pnls/create/${workspaceId}/${strategyId}/${t.Id}`">
                                {{t.Name}}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="mt-3">

                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th></th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="t in rm.Corporate" :key="t.Id">
                            <td>
                                {{t.Name}}
                            </td>
                            <td>
                                <div class="dropdown">
                                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                        Actions
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <a class="dropdown-item" v-sanitizedHref="`/#/enterprise/pnls/edit/${workspaceId}/${strategyId}/${t.Id}`">Edit</a>
                                        <button class="dropdown-item" :disabled="invokingDeletePnlCommandV1" @click="handleDeletePnlCommand(t.Id)">Delete</button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>              
            </div>
        </div>
    </div>
</template>



